import { useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { makeStyles } from '@material-ui/core'

import useClientAddress from '~/address/hooks/useClientAddress'
import { createNewAddressStructure, deleteAddress } from '~/address/store/address.reducer'
import { createBundlesStructure, deleteBundle } from '~/bundles/store/bundles.reducer'
import { createNewLineStructure, deleteLine } from '~/line/store/line.reducer'
import { selectAdditionalLines } from '~/line/store/line.selectors'
import useModal from '~/modal/hooks/useModal'
import { selectIsRestartModal } from '~/modal/store/modal.selectors'
import useResetSale from '~/sale/hooks/useResetSale'
import useScrollToFirstError from '~/sale/hooks/useScrollToFirstError'
import { selectCanContinue, selectIsCustomerValid } from '~/sale/store'
import { deleteForm } from '~/sale/store/sale.reducer'
import { LineTypes } from '~/tariff/model/tariff.model'
import { deleteTariff } from '~/tariff/store/tariff.reducer'

export default function useOfferSetupPage({ nextRoute }: { nextRoute: string }) {
  const dispatch = useDispatch()
  const history = useHistory()
  const { selectResetSaleModalMessage } = useResetSale()
  const deleteClientSearchAddress = useClientAddress()

  const wrapper = useRef(null)

  const { setValdationStatus } = useScrollToFirstError(wrapper)

  const isCustomerValid = useSelector(selectIsCustomerValid)

  const canContinue = useSelector(selectCanContinue)
  const isRestartModal = useSelector(selectIsRestartModal)
  const { onCloseModal } = useModal()

  const additionalLines = useSelector(selectAdditionalLines)
  const newAdditionalLinesId = useMemo(() => getNextAdditionalId(), [additionalLines])

  function getNextAdditionalId(): number {
    const lastId = Object.keys(additionalLines)[Object.keys(additionalLines).length - 1]
    return lastId ? +(lastId.split('').pop() || '0') + 1 : 1
  }

  function addAdditionalLine() {
    const newId = `${LineTypes.ADDITIONAL}${newAdditionalLinesId}`
    dispatch(createNewAddressStructure(newId))
    dispatch(createNewLineStructure(newId))
    dispatch(createBundlesStructure(newId))
  }

  function deletePackage(id: string) {
    dispatch(deleteLine(id))
    dispatch(deleteTariff(id))
    dispatch(deleteAddress(id))
    dispatch(deleteBundle(id))
    dispatch(deleteForm(id))
  }

  function onSubmit() {
    if (canContinue) {
      history.push({
        pathname: nextRoute,
        search: location.search,
      })
    } else {
      setValdationStatus(true)
    }
  }

  return {
    onSubmit,
    deletePackage,
    addAdditionalLine,
    onCloseModal,
    isRestartModal,
    selectResetSaleModalMessage,
    wrapper,
    additionalLines,
    isCustomerValid,
    deleteClientSearchAddress,
  }
}

export const useStyles = makeStyles((theme) => ({
  border: {
    border: `1px solid ${theme.palette.primary.contrastText}`,
    '& h3': {
      cursor: 'pointer',
    },
  },
  overlay: {
    position: 'absolute',
    top: '0',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.grey[50],
    opacity: 0.8,
    borderRadius: '14px',
  },
}))
